import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseResizablePopupOptions } from 'utils/helpers/chatbotHelpers';

export const useResizablePopup = (
  opts: UseResizablePopupOptions = {}
) => {
  const {
    initialWidthPx = 480,
    initialHeightPx = 560,
    minWidthPx = 420,
    minHeightPx = 420,
    leftOffsetPx = 80,
    bottomOffsetPx = 100,
    maxWidthPct = 80,
    maxHeightPct = 80
  } = opts;

  const [widthPct, setWidthPct] = useState<number>(0);
  const [heightPct, setHeightPct] = useState<number>(0);

  const isResizingRef = useRef<boolean>(false);
  const startXRef = useRef<number>(0);
  const startYRef = useRef<number>(0);
  const startWidthPxRef = useRef<number>(0);
  const startHeightPxRef = useRef<number>(0);

  // Initialize to pixel sizes converted to vw/vh and clamp to viewport
  useEffect(() => {
    const init = () => {
      const vw = Math.max(window.innerWidth, 1);
      const vh = Math.max(window.innerHeight, 1);
      const capWidthPx = (Math.max(maxWidthPct, 1) / 100) * vw;
      const capHeightPx = (Math.max(maxHeightPct, 1) / 100) * vh;
      const maxWidthPx = Math.max(Math.min(capWidthPx, vw - leftOffsetPx), minWidthPx);
      const maxHeightPx = Math.max(Math.min(capHeightPx, vh - bottomOffsetPx), minHeightPx);
      const w = Math.min(initialWidthPx, maxWidthPx);
      const h = Math.min(initialHeightPx, maxHeightPx);
      setWidthPct((w / vw) * 100);
      setHeightPct((h / vh) * 100);
    };
    init();
    window.addEventListener('resize', init);
    return () => window.removeEventListener('resize', init);
  }, [initialWidthPx, initialHeightPx, minWidthPx, minHeightPx, leftOffsetPx, bottomOffsetPx, maxWidthPct, maxHeightPct]);

  const onResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizingRef.current = true;
    startXRef.current = e.clientX;
    startYRef.current = e.clientY;

    const vw = Math.max(window.innerWidth, 1);
    const vh = Math.max(window.innerHeight, 1);
    startWidthPxRef.current = (widthPct / 100) * vw;
    startHeightPxRef.current = (heightPct / 100) * vh;

    const handleMove = (moveEvent: MouseEvent) => {
      if (!isResizingRef.current) return;
      const deltaX = moveEvent.clientX - startXRef.current;
      const deltaY = moveEvent.clientY - startYRef.current;

      const vw = Math.max(window.innerWidth, 1);
      const vh = Math.max(window.innerHeight, 1);
      const capWidthPx = (Math.max(maxWidthPct, 1) / 100) * vw;
      const capHeightPx = (Math.max(maxHeightPct, 1) / 100) * vh;
      const maxWidthPx = Math.max(Math.min(capWidthPx, vw - leftOffsetPx), minWidthPx);
      const maxHeightPx = Math.max(Math.min(capHeightPx, vh - bottomOffsetPx), minHeightPx);

      const nextWidthPx = Math.min(
        Math.max(startWidthPxRef.current + deltaX, minWidthPx),
        maxWidthPx
      );
      const nextHeightPx = Math.min(
        Math.max(startHeightPxRef.current - deltaY, minHeightPx),
        maxHeightPx
      );

      setWidthPct((nextWidthPx / vw) * 100);
      setHeightPct((nextHeightPx / vh) * 100);
    };

    const handleUp = () => {
      isResizingRef.current = false;
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  }, [widthPct, heightPct, leftOffsetPx, bottomOffsetPx, minWidthPx, minHeightPx, maxWidthPct, maxHeightPct]);

  const bubbleMaxWidth = useMemo(() => {
    if (typeof window === 'undefined') return 340;
    const vw = Math.max(window.innerWidth, 1);
    const popupWidthPx = (widthPct / 100) * vw;
    return Math.round(popupWidthPx * 0.7);
  }, [widthPct]);

  return {
    widthPct,
    heightPct,
    setWidthPct,
    setHeightPct,
    onResizeStart,
    bubbleMaxWidth
  } as const;
};

export type UseResizablePopupReturn = ReturnType<typeof useResizablePopup>;


